<template>
  <div class="config-creation">
    <v-card>
      <v-card-title>Paramétrage de la nouvelle configuration </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="9">
            <v-text-field
              label="Nom de la configuration"
              v-model="config.name"
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="3">
            <v-switch
              label="Activée"
              v-model="config.enabled"
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <site-filter
              siteListGetter="aiFactory/getSiteConnectors"
              itemValue="site.id"
              itemText="site.name"
              :initialValue="config.site_id"
              @valueChanged="updateSite"
              :noUrl="true"
              :multiple="false"
              :dependsOnSiteGroups="false"
              :zindex="zIndex.modale"
            />
          </v-col>
          <v-col cols="12">
            <time-range-filter
              :initialValue="config.nhours"
              @valueChanged="updateTimeRange"
              :noUrl="true"
            />
          </v-col>
          <v-col cols="12">
            <score-filter
              :initialValue="config.min_score"
              @valueChanged="updateScore"
              :noUrl="true"
            />
          </v-col>
          <v-col cols="12">
            <frequency-filter
              :initialValue="config.frequency_id"
              :forceSiteId="config.site_id"
              @valueChanged="updateFrequency"
              :noUrl="true"
            />
          </v-col>
          <v-col cols="12">
            <v-select
              v-model="config.nb_articles_max"
              :items="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
              label="Nb. d'articles générés max"
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <forced-category-filter
              :initialValue="config.forced_category_id"
              :forceSiteId="config.site_id"
              @valueChanged="updateForcedCategory"
            />
          </v-col>
          <v-col cols="12">
            <wp-author-filter
              :initialValue="config.author_id"
              :forceSiteId="config.site_id"
              @valueChanged="updateAuthor"
            />
          </v-col>
          <v-col cols="12">
            <config-filter
              label="Configurations de sources"
              :site="config.site_id"
              :initialValue="config.config_ids"
              @valueChanged="updateConfigList"
              :noUrl="true"
              :zindex="zIndex.modale"
            />
          </v-col>
          <v-col cols="12">
            <status-filter
              :initialValue="config.status_id"
              @valueChanged="updateStatus"
              :noUrl="true"
            />
          </v-col>
          <v-col cols="12">
            <custom-prompt-template-filter
              :initialValue="config.custom_prompt_template_id"
              :forceSiteId="config.site_id"
              @valueChanged="updateCustomPromptTemplate"
              :noUrl="true"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-row justify="center" class="pt-6 pb-6">
          <v-btn
            class="ml-6 mr-6 secondary"
            @click="saveConfig"
            :disabled="saveDisabled"
            >Enregistrer</v-btn
          >
          <v-btn class="ml-6 mr-6 primary" @click="cancelConfig">Annuler</v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { computed, onMounted, reactive, watch } from "@vue/composition-api";

import useAxios from "@/hooks/useAxios";

import SiteFilter from "@/components/common/filters/SiteFilter";
import ScoreFilter from "@/components/ai-factory/common/filters/ScoreFilter.vue";
import CustomPromptTemplateFilter from "@/components/ai-factory/full-auto/filters/CustomPromptTemplateFilter.vue";
import ForcedCategoryFilter from "@/components/ai-factory/full-auto/filters/ForcedCategoryFilter.vue";
import FrequencyFilter from "@/components/ai-factory/full-auto/filters/FrequencyFilter.vue";
import StatusFilter from "@/components/ai-factory/full-auto/filters/StatusFilter.vue";
import TimeRangeFilter from "@/components/ai-factory/full-auto/filters/TimeRangeFilter.vue";
import WpAuthorFilter from "@/components/ai-factory/full-auto/filters/WpAuthorFilter.vue";
import ConfigFilter from "@/components/news-radar/common/filters/ConfigFilter.vue";
import { zIndex } from "@/utils/constants";

export default {
  name: "ConfigCreation",
  components: {
    ConfigFilter,
    CustomPromptTemplateFilter,
    ForcedCategoryFilter,
    FrequencyFilter,
    SiteFilter,
    ScoreFilter,
    StatusFilter,
    TimeRangeFilter,
    WpAuthorFilter,
  },
  setup(props, { emit }) {
    const { axiosGet, axiosPost } = useAxios();

    const defaultValues = reactive({
      frequency_id: 0,
      status_id: 0,
    });

    const config = reactive({
      name: "",
      site_id: 0,
      frequency_id: 0, // updated onMounted
      config_ids: [],
      min_score: 9,
      status_id: 0, // updated onMounted
      nhours: 1,
      nb_articles_max: 3,
      forced_category_id: null,
      author_id: null,
      custom_prompt_template_id: 0,
      enabled: 1,
    });

    // =>  METHODS
    const updateSite = (value) => {
      config.site_id = value;
    };

    const updateConfigList = (values) => {
      config.config_ids = values;
    };

    const updateTimeRange = (value) => {
      config.nhours = value;
    };

    const updateFrequency = (value) => {
      config.frequency_id = value;
    };

    const updateStatus = (value) => {
      config.status_id = value;
    };

    const updateScore = (value) => {
      config.min_score = value;
    };

    const updateCustomPromptTemplate = (value) => {
      config.custom_prompt_template_id = value;
    };

    const updateForcedCategory = (value) => {
      config.forced_category_id = value;
    };

    const updateAuthor = (value) => {
      config.author_id = value;
    };

    const cancelConfig = async () => {
      config.name = "";
      config.site_id = 0;
      config.frequency_id = defaultValues.frequency?.id ?? 0; // daily
      config.config_ids = [];
      config.min_score = 9;
      config.status_id = defaultValues.status?.id ?? 0; // draft,
      config.nhours = 1;
      config.nb_articles_max = 3;
      config.forced_category_id = null;
      config.author_id = null;
      config.custom_prompt_template_id = 0;
      config.enabled= 1;

      emit("close-dialog");
    };

    const saveConfig = async () => {
      await axiosPost("/aif-auto-configs/", {
        ...config,
        config_ids: config.config_ids.join(","),
        custom_prompt_template_id:
          Number(config.custom_prompt_template_id || 0) > 0
            ? Number(config.custom_prompt_template_id)
            : null,
        forced_category_id:
          Number(config.forced_category_id || 0) > 0
            ? Number(config.forced_category_id)
            : null,
        author_id:
          Number(config.author_id || 0) > 0 ? Number(config.author_id) : null,
      });
      emit("saved");
      emit("close-dialog");
    };

    // =>  COMPUTED
    const saveDisabled = computed(
      () =>
        config.name.length < 3 ||
        !config.site_id ||
        config.config_ids.length === 0
    );

    // =>  WATCH
    watch(defaultValues, () => {
      for (const [key, value] of Object.entries(defaultValues)) {
        config[key] = value;
      }
    });

    // =>  HOOKS
    onMounted(async () => {
      const { data: frequency } = await axiosGet(
        "/aif-auto-configs/frequency/by-name/bidaily"
      );
      const { data: status } = await axiosGet(
        "/meta-options/aif_publication_status/draft/"
      );

      defaultValues.frequency_id = frequency?.id ?? 0; // bidaily
      defaultValues.status_id = status?.id ?? 0; // draft
    });

    // =>  DATA
    return {
      cancelConfig,
      config,
      saveConfig,
      saveDisabled,
      updateAuthor,
      updateConfigList,
      updateCustomPromptTemplate,
      updateForcedCategory,
      updateFrequency,
      updateScore,
      updateSite,
      updateStatus,
      updateTimeRange,
      zIndex,
    };
  },
};
</script>

<style scoped>
.config-creation {
  max-width: 1000px;
  margin: 0 auto;
}
</style>
